import { useEffect, useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { PAGE_USER_PROFILE, RouterParams } from 'containers/AppRouter';
import ButtonActionActiveStatus from 'shared/components/Button/Action/ButtonActionActiveStatus/ButtonActionActiveStatus';
import ButtonActionDelete from 'shared/components/Button/Action/ButtonActionDelete/ButtonActionDelete';
import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import InputForm from 'shared/components/Input/InputForm';
import Loader from 'shared/containers/Loader';
import { APIErrorT } from 'shared/models/api';
import { UserT } from 'shared/models/users';
import api from 'utils/api';
import { useAuth } from 'utils/hooks/providers/auth-context';

import { URLOrganizationPrefix } from '../GenericUsers';
import validator from './GenericUsersManageData.validator';

type FormValues = {
  firstname: string;
  lastname: string;
  email: string;
  username: string;
  phone: string;
  password: string;
  confirmPassword: string;
  admin: boolean;
};
function GenericUsersManageData() {
  const history = useHistory();
  const { username, organizationName } = useParams() as RouterParams;

  const { authState } = useAuth();
  const isConnectedUserProfile = (
    location.pathname === PAGE_USER_PROFILE // eslint-disable-line no-restricted-globals
    || authState.username === username
  );
  const usernameValue = isConnectedUserProfile
    ? authState.username as (string | undefined)
    : username;
  const {
    register,
    handleSubmit,
    getValues,
    setError,
    reset,
    control,
    formState: {
      errors,
      isDirty,
    },
  } = useForm<FormValues>({
    mode: 'onChange',
    resolver: validator,
  });
  const isEdit = usernameValue !== undefined;
  const isAbleRoleUpdate = authState.admin || (authState.multi && !isConnectedUserProfile);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [targetId, setTargetId] = useState('');
  const [isActive, setIsActice] = useState(false);

  const apiCall = organizationName ? new api.OrganizationUsers(organizationName) : api.users;

  useEffect(() => {
    if (isEdit) {
      setIsLoading(true);
      apiCall?.getOne(usernameValue as string)
        .then((resp: Response) => {
          if (resp.status >= 400) {
            resp.json()
              .then((error: APIErrorT) => {
                console.warn(error?.message);
                setIsLoading(false);
                toast.error(`Not possible to edit the user with username '${usernameValue}'`);
                history.goBack();
              });
          } else {
            resp.json()
              .then((data: UserT) => {
                reset(data as any);
                setTargetId(data.id);
                setIsActice(data.active);
                setIsLoading(false);
              });
          }
        });
    }
  }, []);

  const onSubmit: SubmitHandler<FormValues> = (formData: FormValues) => {
    const newData = {
      ...formData,
    };
    setIsSubmitting(true);
    const request = isEdit
      ? apiCall?.update(targetId, newData)
      : apiCall?.register(newData);
    request?.then((resp: any) => {
      if (resp.status >= 400) {
        resp.json()
          .then((error: APIErrorT) => {
            setIsSubmitting(false);
            const message = error?.message;
            if (message.includes('user already exists')) {
              setError('username', { message: 'Username already used' });
              return;
            }
            if (message.includes('User exists with same email')) {
              setError('email', { message: 'Email already linked to another account' });
              return;
            }
            toast.error(message);
          });
      } else {
        resp.json()
          .then((data: UserT) => {
            toast.success(`The user '${data.username}' has been ${isEdit ? 'updated' : 'created'}`);
            setIsSubmitting(false);
            history.push(`${URLOrganizationPrefix(organizationName)}${organizationName ? '#' : '/'}users`);
          });
      }
    });
  };

  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="title__header">
        <h1>{ `${organizationName ? `Organization ${organizationName} ` : ''}${isConnectedUserProfile ? 'My User Profile' : 'User'} · ${isEdit ? 'Edit' : 'Create'} `}</h1>
        <div className="title__header--actions">
          {
            isEdit && !isConnectedUserProfile
              ? (
                <>
                  <ButtonActionActiveStatus
                    apiCall={
                      organizationName
                        ? new api.OrganizationUsers(organizationName)
                        : api.users
                    }
                    targetId={targetId}
                    defaultValue={isActive}
                    name="user"
                  />
                  <ButtonActionDelete
                    targetId={targetId}
                    api={apiCall}
                    messageComplement={`the user '${username}'`}
                    redirectLocation={`${URLOrganizationPrefix(organizationName)}${organizationName ? '#' : '/'}users`}
                  />
                </>
              )
              : null
          }
          <ButtonSubmitData
            isEdit={isEdit}
            isSubmitting={isSubmitting}
            disabled={isEdit && !isDirty}
          />
        </div>
      </div>
      <div className="form scroll">
        <div className="form__section">
          <h3>Account</h3>
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="Username"
            register={register('username')}
            errorField={errors.username}
            disabled={isEdit}
          />
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="Password"
            register={register('password')}
            errorField={errors.password}
            required={!isEdit}
            type="password"
            autoComplete="new-password"
          />
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="Confirm Password"
            register={register('confirmPassword')}
            errorField={errors.confirmPassword}
            type="password"
            autoComplete="new-password"
          />
          <InputForm.Checkbox
            sx={{ ml: 0.5, mt: 1 }}
            color="secondary"
            name="admin"
            control={control}
            label="Admin"
            defaultChecked={getValues().admin}
            disabled={!isAbleRoleUpdate}
            helpText="Adds administrator rights to this user"
          />
        </div>
        <div className="form__section">
          <h3>Personal Info</h3>
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="First name"
            register={register('firstname')}
            errorField={errors.firstname}
          />
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="Last name"
            register={register('lastname')}
            errorField={errors.lastname}
          />
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="E-mail"
            register={register('email')}
            errorField={errors.email}
            type="email"
          />
          <InputForm.TextField
            fullWidth
            sx={{ m: 1 }}
            label="Phone number"
            register={register('phone')}
            errorField={errors.phone}
          />
        </div>
      </div>
    </form>
  );
}

export default GenericUsersManageData;
